import type {
  FloorId,
  NonConformityPlaceId,
  PieceExecutionId,
  RoomId,
  StructuralPieceId,
  UnitPositionId,
  UnitTypeId,
} from '../shared/ids';
import { nomeDaPeca } from '../takeoff/planilha-por-peca';

/**
 * Onde a não conformidade foi encontrada (Fase 37, etapa 3 — D9, RN-516 a RN-521).
 *
 * Quem reprova um item diz, se quiser, **quais** cômodos e peças daquele dia não ficaram
 * conformes — "o banheiro do apto 04", "as estacas E76-A e E77-B". A NC desfaz o que eles
 * diziam: a peça volta a não executada, e o cômodo ao percentual de antes do dia, até ser
 * apontado de novo. Cada lugar guarda o que foi desfeito, para devolver se a NC for cancelada.
 *
 * Só os do dia: a peça marcada executada naquele dia, o cômodo com percentual apontado naquele
 * dia, na frente e no serviço da NC (RN-517). Quem confere isso é o caso de uso, com a obra.
 */

/** O cômodo do apto: pavimento × posição × cômodo, como no avanço por cômodo (RN-441). */
export interface NonConformityRoomPlace {
  readonly id: NonConformityPlaceId;
  readonly kind: 'ROOM';
  readonly floorId: FloorId;
  readonly positionId: UnitPositionId;
  readonly roomId: RoomId;
  /** Cópias para o papel (ADR-128): "Banheiro", do apto "04". */
  readonly roomName: string;
  readonly positionLabel: string;
  /** O percentual que valia antes do dia — o que a NC aponta no cômodo (RN-518). */
  readonly percentBefore: number;
  /** O percentual do dia, que a NC tirou — o que volta se ela for cancelada (RN-521). */
  readonly percentUndone: number;
}

/** A peça: tipologia × nome, como a marca de execução (RN-413). */
export interface NonConformityPiecePlace {
  readonly id: NonConformityPlaceId;
  readonly kind: 'PIECE';
  readonly unitTypeId: UnitTypeId;
  readonly pieceName: string;
  /** A peça desenhada, quando havia. */
  readonly pieceId?: StructuralPieceId;
  /** A marca de execução que a NC tirou — a que volta se ela for cancelada (RN-521). */
  readonly executionId?: PieceExecutionId;
}

export type NonConformityPlace = NonConformityRoomPlace | NonConformityPiecePlace;

/** Quantos lugares cabem numa NC — uma laje de estacas inteira, com folga. */
export const MAX_NON_CONFORMITY_PLACES = 200;

/** `R:posição|cômodo` — o cômodo do apto, dentro do pavimento da NC. */
export function roomPlaceKey(positionId: string, roomId: string): string {
  return `R:${positionId}|${roomId}`;
}

/** `P:tipologia|NOME` — a peça pelo nome normalizado, como a marca (`pieceExecutionKey`). */
export function piecePlaceKey(unitTypeId: string, pieceName: string): string {
  return `P:${unitTypeId}|${nomeDaPeca(pieceName)}`;
}

export function placeKey(place: NonConformityPlace): string {
  return place.kind === 'ROOM'
    ? roomPlaceKey(place.positionId, place.roomId)
    : piecePlaceKey(place.unitTypeId, place.pieceName);
}

/** Os mesmos lugares, em qualquer ordem — o que decide se a NC mudou ao responder de novo. */
export function samePlaces(
  a: readonly NonConformityPlace[] | undefined,
  b: readonly NonConformityPlace[] | undefined,
): boolean {
  const chavesA = new Set((a ?? []).map(placeKey));
  const chavesB = (b ?? []).map(placeKey);
  return chavesA.size === chavesB.length && chavesB.every((chave) => chavesA.has(chave));
}

/** "Banheiro do apto 04" — o cômodo como a tela e o papel o mostram. */
export function roomPlaceLabel(
  place: Pick<NonConformityRoomPlace, 'roomName' | 'positionLabel'>,
): string {
  return `${place.roomName} do apto ${place.positionLabel}`;
}

/** O lugar como a tela e o papel o mostram: o nome da peça, ou o cômodo do apto. */
export function placeLabel(place: NonConformityPlace): string {
  return place.kind === 'ROOM' ? roomPlaceLabel(place) : place.pieceName;
}

/**
 * "E76-A, E77-B · Banheiro do apto 04" — as peças primeiro, na ordem do nome, e os cômodos
 * depois, por apto. Vazio sem lugar.
 */
export function describePlaces(places: readonly NonConformityPlace[] | undefined): string {
  const lista = places ?? [];
  const pecas = lista
    .filter((place): place is NonConformityPiecePlace => place.kind === 'PIECE')
    .map((place) => place.pieceName)
    .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
  const comodos = lista
    .filter((place): place is NonConformityRoomPlace => place.kind === 'ROOM')
    .sort(
      (a, b) =>
        a.positionLabel.localeCompare(b.positionLabel, 'pt-BR', { numeric: true }) ||
        a.roomName.localeCompare(b.roomName, 'pt-BR'),
    )
    .map(roomPlaceLabel);
  return [pecas.join(', '), comodos.join(', ')].filter((parte) => parte !== '').join(' · ');
}
